import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, Checkbox, ClearableInput, Spinner } from '@rio-cloud/rio-uikit';
import { Consultant, ConsultantFormData } from '../../../../models/consultant';
import ErrorDialog from '../../../../components/dialogs/ErrorDialog';

interface ConsultantFormProps {
  consultant?: Consultant | null;
  editConsultant: (id: string, data: ConsultantFormData) => Promise<boolean>;
  addConsultant: (data: ConsultantFormData) => Promise<boolean>;
  onSuccess: () => void;
  onCancel: () => void;
}

const ConsultantForm: React.FC<ConsultantFormProps> = ({
  consultant,
  editConsultant,
  addConsultant,
  onSuccess,
  onCancel,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorDialog, setErrorDialog] = useState({ show: false, title: '', body: '' });

  const [formData, setFormData] = useState<ConsultantFormData>({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dn: '',
    specialist: true,
    groupConsultant: false,
    sendEmail: true
  });

  useEffect(() => {
    if (consultant) {
      setFormData({
        id: consultant.id ?? '',
        firstName: consultant.firstName,
        lastName: consultant.lastName,
        email: consultant.email,
        phoneNumber: consultant.phoneNumber,
        dn: consultant.dn,
        specialist: consultant.specialist,
        sendEmail: consultant.sendEmail,
        groupConsultant: consultant.groupConsultant
      });
    }
  }, [consultant]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) {
      newErrors.id = intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.idRequired' });
    } else if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(formData.id.trim())) {
      newErrors.id = intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.idInvalid' });
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.firstNameRequired' });
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.lastNameRequired' });
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.emailInvalid' });
    }

    if (!formData.dn.trim()) {
      newErrors.dn = intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.dnRequired' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let success = false;

      if (consultant?.id) {
        success = await editConsultant(consultant.id, formData);
      } else {
        success = await addConsultant(formData);
      }

      if (success) {
        onSuccess();
      }
    } catch (error: any) {
      setErrorDialog({
        show: true,
        title: intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.saveErrorTitle' }),
        body: error.message || intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.saveErrorBody' }),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ConsultantFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }

  };


    const handleChekboxChange = (field: keyof ConsultantFormData, event: any) => {
      const isChecked = event.target.checked
    setFormData(prev => ({
      ...prev,
      [field]: isChecked,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-size-h3">{intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.consultantData' })}</div>
        <div>
          <div>
            <label htmlFor="id" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
              {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.idLabel' })}
            </label>
            <ClearableInput
              id="id"
              value={formData.id}
              onChange={(value) => handleInputChange('id', value)}
              placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.idPlaceholder' })}
              disabled={!!consultant?.id}
            />
            {errors.id && (
              <p className="text-color-danger text-size-12 mt-1">{errors.id}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="firstName" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.firstNameLabel' })}
          </label>
          <ClearableInput
            id="firstName"
            value={formData.firstName}
            onChange={(value) => handleInputChange('firstName', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.firstNamePlaceholder' })}
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.lastNameLabel' })}
          </label>
          <ClearableInput
            id="lastName"
            value={formData.lastName}
            onChange={(value) => handleInputChange('lastName', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.lastNamePlaceholder' })}
          />
        </div>

        <div>
          <label htmlFor="email" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.emailLabel' })}
          </label>
          <ClearableInput
            id="email"
            value={formData.email}
            onChange={(value) => handleInputChange('email', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.emailPlaceholder' })}
          />
        </div>

        <div>
          <label htmlFor="phone" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.phoneLabel' })}
          </label>
          <ClearableInput
            id="phone"
            value={formData.phoneNumber}
            onChange={(value) => handleInputChange('phoneNumber', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.phonePlaceholder' })}
          />
        </div>

        <div>
          <label htmlFor="dealershipCode" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.dnLabel' })}
          </label>
          <ClearableInput
            id="dealershipCode"
            value={formData.dn}
            onChange={(value) => handleInputChange('dn', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.dnPlaceholder' })}
          />
        </div>
        
        <div>
          <label htmlFor="specialist" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.specialist' })}
          </label>
          <Checkbox
            id="specialist"
            checked={formData.specialist}
            onChange={(event) => {handleChekboxChange('specialist', event)}}
          />
        </div>

        <div>
          <label htmlFor="groupConsultant" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.groupConsultant' })}
          </label>
          <Checkbox
            id="groupConsultant"
            checked={formData.groupConsultant}
            onChange={(event) => {handleChekboxChange('groupConsultant', event)}}
          />
        </div>

        <div>
          <label htmlFor="sendEmail" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.sendEmail' })}
          </label>
          <Checkbox
            id="sendEmail"
            checked={formData.sendEmail}
            onChange={(event) => {handleChekboxChange('sendEmail', event)}}
          />
        </div>
       
      </div> 

      <div className="flex justify-end space-x-3 pt-6">
        <Button
          bsStyle={Button.DEFAULT}
          onClick={onCancel}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.cancel' })}
        </Button>
        <Button
          bsStyle={Button.PRIMARY}
          type="submit"
          disabled={loading}
          iconName={loading ? undefined : "rioglyph-check"}
        >
          {loading ? (
            <div className="flex items-center">
              <Spinner />
              {consultant ? intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.saving' }) : intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.creating' })}
            </div>
          ) : (
            consultant ? intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.save' }) : intl.formatMessage({ id: 'intl-msg:backoffice.consultantForm.create' })
          )}
        </Button>
      </div>
      <ErrorDialog
        show={errorDialog.show}
        title={errorDialog.title}
        body={errorDialog.body}
        onClose={() => setErrorDialog({ show: false, title: '', body: '' })}
      />
    </form>
  );
};

export default ConsultantForm;
